<template>
  <BundleSignupShell>
    <p class="text-center text-xs font-bold tracking-widest uppercase text-gray-400 mb-1.5">
      Step 2 of 3 · Billing
    </p>
    <h1 class="text-center text-xl font-medium text-gray-900 mb-4">Payment Details</h1>

    <div class="border border-gray-100 rounded-xl overflow-hidden mb-4">
      <div class="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700">
        <Logo type="ICON_DARK" class="h-4 w-4 object-contain" />
        <b class="text-gray-900 font-semibold">Coach Catalyst — Pro</b>
        <span class="ml-auto text-xs font-bold tracking-wider uppercase text-cc-green-600 bg-cc-green-100 px-2 py-0.5 rounded-full">Included</span>
      </div>
      <div class="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 border-t border-gray-100">
        <img :src="gcLogo" class="h-4 w-4 object-contain" alt="" />
        <b class="text-gray-900 font-semibold">Growth Catalyst — CRM</b>
        <span class="ml-auto text-xs font-bold tracking-wider uppercase text-cc-gc-purple-deep bg-cc-gc-purple-soft px-2 py-0.5 rounded-full">Included</span>
      </div>
      <div class="flex items-baseline justify-between px-4 py-3 border-t border-gray-100 bg-cc-lightest-purple text-sm text-gray-500">
        <span>Billed monthly</span>
        <span class="text-lg font-extrabold text-gray-900">$299<small class="text-xs font-semibold text-gray-400">/mo</small></span>
      </div>
    </div>

    <div class="flex items-center gap-2.5 text-xs font-bold tracking-wider uppercase text-gray-400 mt-4 mb-3">
      Card details
      <span class="flex-1 h-px bg-gray-100"></span>
    </div>

    <div class="mb-3">
      <label class="block text-sm font-semibold text-gray-900 mb-1.5">Card</label>
      <div class="w-full box-border px-4 py-3 bg-white border border-gray-300 rounded-lg">
        <div ref="card"></div>
      </div>
    </div>

    <button
      type="button"
      :class="ctaClass"
      :disabled="disableButton"
      @click="submit"
    >
      <span
        v-if="processing"
        class="w-4 h-4 rounded-full border-2 border-white border-opacity-40 border-t-white animate-spin"
      ></span>
      {{ processing ? 'Processing…' : 'Pay $299 & Create Account' }}
    </button>

    <div class="flex items-center justify-center flex-wrap gap-3 mt-3 text-xs font-semibold text-gray-500">
      <span class="inline-flex items-center gap-1.5">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-cc-green-500">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Encrypted &amp; secure
      </span>
    </div>

    <button type="button" class="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-gray-400 hover:text-gray-700" @click="back">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <path d="M15 18l-6-6 6-6" />
      </svg>
      Back to your details
    </button>
  </BundleSignupShell>
</template>

<script>
  import { mapState } from 'vuex';
  import ApiUrls from '../../../api-url';
  import AuthenticationMixin from '../../../authentication-mixin';
  import getters from '../../../stores/login-store/getters/login-getters';
  import { trackEvent } from '../../../services/analytics';
  import { trackSignupCompleted } from '../../../services/gtm.js';
  import configService from '../../../services/config-service.js';
  import Logo from '../../../components/logos/Logo.vue';
  import BundleSignupShell from './bundle-signup-shell.vue';

  const cardStyle = {
    base: { color: '#0f1224', fontFamily: 'Inter, sans-serif', fontSize: '15px' },
  };
  let stripe = Stripe(configService.getStripeConfig().publishableKey);
  let elements = stripe.elements();
  let card;

  export default {
    name: 'BundleSignupBilling',
    mixins: [ApiUrls, AuthenticationMixin],
    components: { Logo, BundleSignupShell },
    data() {
      return {
        gcLogo: require('images/growth_catalyst_logo.png'),
        disableButton: true,
        processing: false,
        ctaClass:
          'w-full flex items-center justify-center gap-2 mt-4 px-4 py-2.5 text-base font-semibold text-white bg-cc-green-500 rounded-md hover:brightness-105 transition disabled:opacity-70 disabled:cursor-default',
      };
    },
    computed: {
      ...mapState(['userForm']),
    },
    created() {
      if (!this.userForm || !this.userForm.email) {
        this.$store.commit('setSelectedStep', getters.BUNDLE_SIGNUP_DETAILS());
      }
    },
    mounted() {
      this.$nextTick(() => {
        card = elements.create('card', { style: cardStyle });
        card.mount(this.$refs.card);
        card.on('change', (event) => {
          this.disableButton = !event.complete;
        });
      });
    },
    beforeUnmount() {
      if (card) card.destroy();
    },
    methods: {
      back() {
        this.$store.commit('setSelectedStep', getters.BUNDLE_SIGNUP_DETAILS());
      },
      redirectToSetPassword(data) {
        const q = new URLSearchParams({
          reset_password_token: data.reset_password_token || '',
          first_name: this.userForm.first_name || '',
          last_name: this.userForm.last_name || '',
          email: this.userForm.email || '',
          bundle: '1',
        });
        window.location.href = `/auth/password/edit?${q.toString()}`;
      },
      submit() {
        if (this.disableButton || this.processing) return;
        this.processing = true;
        const self = this;
        trackEvent('bundle_signup_payment_attempted', { email: this.userForm.email });

        grecaptcha.ready(function () {
          grecaptcha
            .execute('6LfiNc4ZAAAAAA7O27dikuMBReTDKH_QeP6sLmxn', { action: 'submit' })
            .then(
              async (recaptchaToken) => {
                try {
                  const stripeResult = await stripe.createToken(card);
                  if (stripeResult.error) {
                    self.failPayment(stripeResult.error.message);
                    return;
                  }

                  let result;
                  try {
                    result = await self.axiosInstance.post('/auth/bundle_signup', {
                      user: {
                        first_name: self.userForm.first_name,
                        last_name: self.userForm.last_name,
                        email: self.userForm.email,
                        phone: self.userForm.phone,
                        business_name: self.userForm.business_name,
                        street: self.userForm.street,
                        city: self.userForm.city,
                        state: self.userForm.state,
                        zip: self.userForm.zip,
                        country: self.userForm.country,
                        organization: { referral: '', referral_text: null, linkmink: null },
                      },
                      stripe_token: stripeResult.token,
                      recaptcha_token: recaptchaToken,
                    });
                  } catch (err) {
                    self.failPayment(
                      err?.response?.data?.error || 'Payment failed. Please try again.'
                    );
                    return;
                  }

                  if (result.data && result.data.success) {
                    trackEvent('bundle_signup_payment_succeeded', { email: self.userForm.email });
                    // Google Ads conversion point for the bundle flow. Fired
                    // before redirectToSetPassword, which leaves the page.
                    trackSignupCompleted(
                      {
                        accountType: 'coach',
                        plan: 'bundle_pro',
                        signupFlow: 'bundle',
                      },
                      () => self.redirectToSetPassword(result.data)
                    );
                  } else {
                    self.failPayment(result.data?.error || 'Payment failed. Please try again.');
                  }
                } catch (e) {
                  self.failPayment(e?.message || 'Payment failed. Please try again.');
                }
              },
              () => {
                self.failPayment('Could not verify reCAPTCHA. Please try again.');
              }
            );
        });
      },
      failPayment(message) {
        this.processing = false;
        trackEvent('bundle_signup_payment_failed', { email: this.userForm.email, reason: message });
        this.$toast.show(message, this.toasted_options.error);
      },
    },
  };
</script>
